/**
 * The typed demographic attributes reporting reads.
 *
 * Projection of `config/reporting-demographic-attributes.json`; the Python
 * owner is `apps/python-api/canonical_analysis/demographic_attributes.py`, and
 * catalogue row `vocab.reporting.demographic_dimension` holds the two in step.
 * An attribute is age because it is DECLARED age (its segmentation mapping
 * names the canonical age variable, or its key is a declared age key), never
 * because a column name contains "age".
 */
import manifest from "../../../config/reporting-demographic-attributes.json";

export type DemographicDimensionId = "gender" | "age" | "country";

export const DEMOGRAPHIC_DIMENSION_IDS: ReadonlyArray<DemographicDimensionId> = [
  "gender",
  "age",
  "country",
];

export interface DemographicDimension {
  id: DemographicDimensionId;
  label: string;
  canonicalId: string;
  valueKind: "categorical" | "age";
  keys: ReadonlyArray<string>;
}

export interface AgeBand {
  label: string;
  min: number;
  /** Null is open-ended. */
  max: number | null;
}

export const DEMOGRAPHIC_DIMENSIONS: ReadonlyArray<DemographicDimension> =
  manifest.dimensions as ReadonlyArray<DemographicDimension>;

export const AGE_BANDS: ReadonlyArray<AgeBand> = manifest.ageBands.bands;

export const AGE_BELOW_FIRST_BAND_LABEL: string =
  manifest.ageBands.belowFirstBandLabel;

export function demographicDimension(
  id: DemographicDimensionId,
): DemographicDimension {
  const found = DEMOGRAPHIC_DIMENSIONS.find((item) => item.id === id);
  if (!found) {
    throw new Error(`undeclared demographic dimension ${id}`);
  }
  return found;
}

/**
 * Which declared dimension an attribute is: by its mapping's canonical variable
 * first, else by an exact declared key. Mirrors `declared_dimension_id`.
 */
export function declaredDemographicDimensionId(args: {
  key?: string | null;
  canonicalId?: string | null;
}): DemographicDimensionId | null {
  const { key, canonicalId } = args;
  if (canonicalId) {
    const byCanonical = DEMOGRAPHIC_DIMENSIONS.find(
      (item) => item.canonicalId === canonicalId,
    );
    if (byCanonical) {
      return byCanonical.id;
    }
  }
  if (key) {
    const byKey = DEMOGRAPHIC_DIMENSIONS.find((item) => item.keys.includes(key));
    if (byKey) {
      return byKey.id;
    }
  }
  return null;
}

/**
 * The declared band a numeric age falls in. Mirrors `age_band_for`: below the
 * first band is its own label, and the bands chain upper-inclusive.
 */
export function ageBandFor(value: number): string {
  if (AGE_BANDS.length === 0 || value < AGE_BANDS[0].min) {
    return AGE_BELOW_FIRST_BAND_LABEL;
  }
  for (const band of AGE_BANDS) {
    if (band.max === null || value <= band.max) {
      return band.label;
    }
  }
  return AGE_BANDS[AGE_BANDS.length - 1].label;
}
